<template>
  <ModalCreateEditEvent
    v-show="modalInfoEvent"
    :event="event"
    @close="closeInformationEventModal"
    @create="createEventTransaction"
    @edit="editEventTransaction"
  />
  <ModalConfirmation
    :message="confirmMessage"
    @accept="closeConfirmModalAsync(deleteEventTransaction)"
    @deny="closeConfirmModalAsync"
    v-show="showConfirmModal"
  />
  <div class="wrapper-events">
    <div class="wrapper-empty" v-if="!event">
      <div class="create-event">
        <p>Eventos</p>
        <StandardButton :buttonLabel="'Crear evento'" @click="createNewEvent" />
      </div>

      <div class="empty">
        <img
          src="@/assets/icons/NoResults.svg"
          alt="Sin resultados"
          class="icon"
        />
        <p class="text-empty">No hay eventos creados</p>
      </div>
    </div>
    <div class="event-full" v-else>
      <div class="above-table-event">
        <p class="title">Eventos - {{ event.name }}</p>

        <a :href="route" class="wrapper-button">
          <StandardButton :buttonLabel="'Descargar excel'" class="button" />
        </a>
        <StandardButton
          :buttonLabel="'Editar evento'"
          class="add-guest"
          @click="editEvent"
          :type="'Edit'"
        />
        <StandardButton
          :buttonLabel="'Eliminar evento'"
          class="delete-event"
          @click="openConfirmDeleteModal"
          :type="'Delete'"
        />

        <Searcher
          v-model:search="query"
          :isDisabled="false"
          placeholder="Busca por cliente (código o nombre) o invitado (documento o nombres)..."
          class="searcher"
        />
        <StandardButton
          :buttonLabel="'Todos los invitados'"
          :isFilter="true"
          class="filter1"
          @click="changeFilterState(0)"
        />
        <StandardButton
          :buttonLabel="'Registrados'"
          :isFilter="true"
          class="filter2"
          @click="changeFilterState(2)"
        />
        <StandardButton
          :buttonLabel="'Sin registrar'"
          :isFilter="true"
          class="filter3"
          @click="changeFilterState(1)"
        />
      </div>

      <div class="info-event">
        <p class="yellow">Fecha máxima de registro:</p>
        <p class="light">{{ formatDate(event.lastDate) }}</p>
        <p class="yellow space">Máximo de invitados por cliente:</p>
        <p class="light">{{ event.maxInvites }}</p>
      </div>

      <table v-if="filteredGuests.length > 0">
        <thead>
          <tr>
            <th>Cod. cliente</th>
            <th>Cliente</th>
            <th>Tipo doc.</th>
            <th>No. documento</th>
            <th>Nombres</th>
            <th>Cargo</th>
            <th>Registrado</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="guest in filteredGuests" :key="guest.document">
            <td>{{ guest.clientCode }}</td>
            <td>{{ guest.clientName }}</td>
            <td>{{ guest.documentType }}</td>
            <td>{{ guest.document }}</td>
            <td>{{ guest.guestName }}</td>
            <td>{{ guest.position }}</td>
            <td>
              <input
                type="checkbox"
                class="check"
                :checked="isRegistered(guest)"
                :disabled="isRegistered(guest)"
                @change="checkGuestTransaction(guest)"
              />
            </td>
          </tr>
        </tbody>
      </table>

      <div class="wrapper-empty-guests" v-else>
        <div class="empty-guests">
          <img
            src="@/assets/icons/NoResults.svg"
            alt="Sin resultados"
            class="icon"
          />
          <p class="text-empty">No hay invitados</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from "vue";
import StandardButton from "@/components/commons/controls/StandardButton.vue";
import Searcher from "@/components/commons/controls/Searcher.vue";
import ModalCreateEditEvent from "./components/ModalCreateEditEvent.vue";
import { eventsUse } from "@/ux/events/use/eventsUse";
import { spinnerProcessKey, successProcessKey } from "@/main";
import { confirmCancelMessageManager } from "@/ux/utils/managers/confirmCancelMessageManager";
import ModalConfirmation from "@/components/commons/modals/ModalConfirmation.vue";
import { Guest } from "@/business/events/domain/events";

export default defineComponent({
  name: "EventsView",
  components: {
    StandardButton,
    Searcher,
    ModalCreateEditEvent,
    ModalConfirmation,
  },

  setup() {
    const successProcess = inject(successProcessKey)!;
    const { showSuccessModal } = successProcess;

    const spinnerProcess = inject(spinnerProcessKey)!;
    const { offSpinnerState, onSpinnerState } = spinnerProcess;

    const {
      showConfirmModal,
      openConfirmModal,
      closeConfirmModalAsync,
      confirmMessage,
    } = confirmCancelMessageManager();

    const {
      modalInfoEvent,
      closeInformationEventModal,
      event,
      createNewEvent,
      editEvent,
      createEventOperation,
      editEventOperation,
      deleteEventOperation,
      filteredGuests,
      query,
      route,
      checkGuestOperation,
      isRegistered,
      changeFilterState,
    } = eventsUse();

    async function createEventTransaction(eventForm: {
      name: string;
      date: string;
      guests: string;
    }) {
      try {
        await onSpinnerState();
        await createEventOperation(
          eventForm.name,
          eventForm.date,
          eventForm.guests
        );
        await offSpinnerState();
        showSuccessModal("Evento creado correctamente");
      } catch (exception) {
        await offSpinnerState();
        throw exception;
      }
    }

    async function editEventTransaction(eventForm: {
      name: string;
      date: string;
      guests: string;
    }) {
      try {
        await onSpinnerState();
        await editEventOperation(
          eventForm.name,
          eventForm.date,
          eventForm.guests
        );
        await offSpinnerState();
        showSuccessModal("Evento actualizado correctamente");
      } catch (exception) {
        await offSpinnerState();
        throw exception;
      }
    }

    async function openConfirmDeleteModal() {
      openConfirmModal("¿Estás seguro de eliminar el evento?");
    }

    async function deleteEventTransaction(): Promise<void> {
      try {
        await onSpinnerState();
        await deleteEventOperation();
        await offSpinnerState();
        showSuccessModal("El evento ha sido eliminado correctamente");
      } catch (exception) {
        await offSpinnerState();
        throw exception;
      }
    }

    async function checkGuestTransaction(guest: Guest) {
      try {
        await onSpinnerState();
        await checkGuestOperation(guest);
        await offSpinnerState();
        showSuccessModal("Invitado registrado correctamente");
      } catch (exception) {
        await offSpinnerState();
        throw exception;
      }
    }

    function formatDate(date: string): string {
      if (!date) return "";

      const [year, month, day] = date.split("-");

      const dateObj = new Date(Number(year), Number(month) - 1, Number(day));

      return dateObj.toLocaleDateString("es-CO", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    }

    return {
      modalInfoEvent,
      createEventTransaction,
      editEventTransaction,
      openConfirmDeleteModal,
      deleteEventTransaction,
      showConfirmModal,
      closeConfirmModalAsync,
      confirmMessage,
      event,
      closeInformationEventModal,
      createNewEvent,
      editEvent,
      formatDate,
      filteredGuests,
      query,
      route,
      checkGuestTransaction,
      isRegistered,
      changeFilterState,
    };
  },
});
</script>

<style scoped lang="scss">
@import "src/ux/events/styles/eventsStyle";
</style>
